import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertObligationOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertObligationOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type InsertObligationOversightResultInput = {
  OversightAssessmentId: string;
  ObligationIds: string[];
  // COI-650: V2 required Rating on this insert; the form schema has always
  // made it mandatory, so the type now matches.
  Rating: number;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertObligationOversightResult = createMutationHook({
  name: 'insertObligationOversightResult',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.assessment.insertObligationOversightResult.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertObligationOversightResultInput) => input,
  invalidate: (trpc) => [
    // Apollo parity: evictField(cache, 'obligation_oversight_result')
    trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
    trpc.frontend.assessment.oversightResultById.pathKey(),
    trpc.frontend.assessment.obligationOversightResultById.pathKey(),
    trpc.frontend.assessment.oversightAssessmentObligationAssessmentResultsByObligationId.pathKey(),
    // Apollo parity: evictField(cache, 'obligation_oversight_result_aggregate')
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    // Apollo parity: evictField(cache, 'oversight_assessment')
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
    trpc.frontend.assessment.oversightActivityCount.queryKey(),
  ],
  mapResponse: (result: {
    Ids: string[];
  }): InsertObligationOversightResultMutation => ({
    insertChildObligationOversightResult: {
      __typename: 'InsertChildObligationAssessmentResultOutput',
      Ids: result.Ids,
    },
  }),
  graphqlDocument: InsertObligationOversightResultDocument,
  graphqlVariables: (input: InsertObligationOversightResultInput) => input,
  evictFields: [
    'obligation_oversight_result',
    'oversight_assessment',
    'obligation_oversight_result_aggregate',
  ],
});
